<template>
  <v-row no-gutters class="eventsLog">
    <v-col cols="9" v-if="isFetched">
      <v-row no-gutters align="center" class="pb-3">
        <v-col cols="7">
          <v-row no-gutters align="center">
            <v-col cols="auto" class="mr-2">
              <v-icon color="rgba(0,0,0,0.7)">
                mdi-calendar
              </v-icon>
            </v-col>
            <v-col cols="6" class="mr-4">
              <data-input v-model="dateType" v-bind="dateTypeInput" />
            </v-col>
            <v-col cols="4">
              <data-input v-model="selectType" v-bind="selectTypeInput" />
            </v-col>
          </v-row>
        </v-col>
        <v-spacer />
        <v-col cols="3">
          <data-input v-model="searchType" v-bind="searchTypeInput" />
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-data-table
            disable-sort
            :headers="headers"
            :items="items"
            :footer-props="{
              itemsPerPageOptions: [5, 10, 20],
            }"
            :options.sync="options"
            :server-items-length="totalItems"
            style="border: 1px solid #DDD"
          >
            <template #item.icon="{ item }">
              <v-icon small :color="setIconColor(item.type)">
                mdi-alert-circle
              </v-icon>
            </template>
            <template #item.details="{item}">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  <v-icon small v-on="on" class="icon_details" @click="getEventLogDetails(item.id)">
                    mdi-clipboard-search
                  </v-icon>
                </template>
                <span>{{ $i18n.t('sidebar.eventslog.checkDetails') }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <main-large-dialog
        :is-visible="isVisible"
        :title="$i18n.t('dialog.title.eventDetails')"
        :has-agree="false"
        header-shadow
        disagree-text="dialog.close"
        @modalClosed="onClose"
      >
        <template #body>
          <template v-if="isLoaded">
            <v-row class="text-left">
              <v-col>
                {{ eventDetails }}
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <main-loader />
          </template>
        </template>
      </main-large-dialog>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import { call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'EventsLogTab',
  components: {
    DataInput,
    MainLargeDialog,
    MainLoader,
  },
  data: () => ({
    isFetched: false,
    isVisible: false,
    isLoaded: false,
    dateType: [],
    selectType: undefined,
    searchType: '',
    eventDetails: '',
    items: [],
    totalItems: 0,
    options: {
      page: 1,
      itemsPerPage: 5,
    },
  }),
  watch: {
    dateType() {
      this.init();
    },
    selectType() {
      this.init();
    },
    searchType() {
      this.init();
    },
    options: {
      handler() {
        this.init();
      },
      deep: true,
    },
  },
  created() {
    this.init();
  },
  computed: {
    dateTypeInput() {
      return {
        dataType: { name: 'daterange' },
        label: this.$i18n.t('sidebar.eventslog.dateLabel'),
        returnType: 'date',
        hideDetails: true,
        clearable: true,
      };
    },
    selectTypeInput() {
      return {
        dataType: { name: 'select' },
        items: this.selectTypes,
        label: this.$i18n.t('sidebar.eventslog.typeLabel'),
        hideDetails: true,
        clearable: true,
      };
    },
    selectTypes() {
      return [
        {
          value: 'EVENT',
          text: this.$i18n.t('admin.eventsLog_table.items.EVENT'),
        },
        {
          value: 'ERROR',
          text: this.$i18n.t('admin.eventsLog_table.items.ERROR'),
        },
        {
          value: 'INFO',
          text: this.$i18n.t('admin.eventsLog_table.items.INFO'),
        },
      ];
    },
    searchTypeInput() {
      return {
        dataType: { name: 'text' },
        prependInnerIcon: 'mdi-magnify',
        placeholder: this.$i18n.t('default.find'),
        hideDetails: true,
        clearable: true,
      };
    },
    headers() {
      const inputs = ['icon', 'id', 'type', 'name', 'date', 'description', 'details'];
      return [
        ...inputs.map(value => {
          return {
            text: this.$i18n.t(`admin.eventsLog_table.headers.${value}`),
            value: value,
            align: 'center',
          };
        }),
      ];
    },
    getSearchedItems() {
      return {
        '!OR': [
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_event_logs.id': { value: this.searchType ? this.searchType : '' },
            },
          },
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_event_logs.name': { value: this.searchType ? this.searchType : '' },
            },
          },
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_event_logs.description': { value: this.searchType ? this.searchType : '' },
            },
          },
          {
            $LIKE_CASE_INSENSITIVE: {
              'events_event_logs.details': { value: this.searchType ? this.searchType : '' },
            },
          },
        ],
      };
    },
  },
  methods: {
    getEventLog: call('admin/getEventLog'),
    getEventsLog: call('admin/getEventsLog'),
    async init() {
      const { page, itemsPerPage } = this.options;
      const payload = {
        features_filter: {
          '!AND': [
            this.getSearchedItems,
            ...(this.dateType?.length === 2
              ? [
                  {
                    $GREATER_OR_EQUAL: {
                      'events_event_logs.date': { value: this.dateType[0] },
                    },
                  },
                  {
                    $LESS_OR_EQUAL: {
                      'events_event_logs.date': { value: this.dateType[1] },
                    },
                  },
                ]
              : []),
            ...(this.selectType
              ? [
                  {
                    $EQUAL: {
                      'events_event_logs.type': { value: this.selectType },
                    },
                  },
                ]
              : []),
          ],
        },
        params: {
          limit: itemsPerPage,
          page: page - 1,
          with_total_count: true,
        },
      };
      try {
        const r = await this.getEventsLog(payload);
        this.items = await r.data.data.features.features.map(el => {
          return {
            ...el.properties,
            id: el.id,
            type: this.$i18n.t(`admin.eventsLog_table.items.${el.properties.type}`),
            date: el.properties.date.replace('T', ' ').slice(0, -4),
          };
        });
        this.totalItems = r.data.data.total_features_count;
        this.isFetched = true;
      } catch (error) {
        console.log('error:', error);
      }
    },
    async getEventLogDetails(id) {
      this.isVisible = true;
      try {
        const r = await this.getEventLog(id);
        this.eventDetails = r.data.details;
        this.isLoaded = true;
      } catch (error) {
        console.log('error:', error);
      }
    },
    setIconColor(type) {
      if (type === 'Błąd') {
        return 'red';
      } else if (type === 'Informacja') {
        return 'blue';
      } else if (type === 'Zdarzenie') {
        return 'orange';
      } else {
        return 'grey';
      }
    },
    onClose() {
      this.isVisible = false;
      this.isLoaded = false;
      this.eventDetails = '';
    },
  },
};
</script>

<style lang="scss">
.eventsLog {
  .icon_details {
    cursor: pointer;

    &:hover {
      color: black;
    }
  }
  .container {
    display: none;
  }
  .v-text-field__slot {
    font-size: 16px;
  }
  .v-input__append-inner {
    padding-left: 0 !important;
  }
}
</style>
